import { EntityType } from '~/enums/EntityType';
import {
  FieldModel,
  FieldModelConfigState,
} from '~/features/x-ray/fields/types';
import { intlService } from '~/i18n/IntlService';
import { sylvanus } from '~/services/serviceInstances';
import { Entity } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { PromptModel, PromptModelConfig } from '../../types/sylvanus/model';
import { createPromptModelVersion } from './createPromptModelVersion';
import { withQueryFormat } from './searchScopeUtils';
import { createFirstTestCase } from './testCaseUtils';
import {
  mapFieldClassificationConfigAttributes,
  mapModelConfigAttributes,
  mapModelVersionToFieldModel,
} from './transformers';

interface Request {
  entity: Entity<EntityType>;
}
type Response = PromptModel;

const createPromptModel = async ({ entity }: Request): Promise<Uuid> => {
  const response = (await sylvanus.post('/model', {
    target_entity: entity,
  })) as Response;

  return response.id;
};

export const savePromptModelDraft = async ({
  modelId,
  config,
  highlightMethod,
  instructionsOptional,
}: {
  modelId: Nullable<Uuid>;
  config: FieldModelConfigState;
  highlightMethod: string;
  instructionsOptional?: boolean;
}): Promise<FieldModel> => {
  const { field, instructions, internal, query } = config;
  if (!field) {
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'savePromptModelDraft.missingField',
        defaultMessage: 'Cannot save model draft: Missing field.',
        description: 'Error message when field is missing for model draft',
      }),
    );
  }

  if (!instructions && !instructionsOptional) {
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'savePromptModelDraft.missingInstructions',
        defaultMessage: 'Cannot save model draft: Missing instructions.',
        description:
          'Error message when instructions are missing for model draft',
      }),
    );
  }

  if (!modelId) {
    modelId = await createPromptModel({
      entity: {
        id: String(field.id),
        type: EntityType.Field,
      },
    });
  }

  const configParams = {
    field_name: field.label,
    instructions,
    internal,
    scope: {
      data: {
        attributes: {
          filters: [],
          query: withQueryFormat(query),
          store_as_recent_search: false,
        },
      },
      type: 'document',
    },
    string_matching_level: config.stringMatchingLevel,
    ...mapModelConfigAttributes(config),
    ...mapFieldClassificationConfigAttributes(config),
  };

  const response = await createPromptModelVersion({
    modelId,
    config: configParams as PromptModelConfig,
  });

  const modelVersion = mapModelVersionToFieldModel({
    modelId,
    version: response,
    fieldId: field.id,
    highlightMethod,
    optimizeState: null,
    publishStatus: null,
  });

  try {
    await createFirstTestCase(modelVersion, highlightMethod);
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail, { cause: modelId });
    } else {
      throw error;
    }
  }

  return modelVersion;
};
